import APIClient, {
  DeviceHeartbeat,
  DeviceHeartbeatRequest,
} from '@raydiant/api-client-js';
import { useState, useEffect, useCallback, useMemo } from 'react';
import Pubnub from 'pubnub';
import handleDeviceDeleted from '../utilities/handleDeviceDeleted';

export default function useHeartbeat(
  apiClient: APIClient,
  pubnubClient: Pubnub,
  onDeviceDeleted: () => void,
  logger: Logger,
): [DeviceHeartbeat | null, Error | null, (interval: number | null) => void] {
  const [result, setResult] = useState<DeviceHeartbeat | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [heartbeatRequest, setHeartbeatRequest] = useState<
    DeviceHeartbeatRequest
  >({
    connection_type: null,
    ssid: null,
    rssi: null,
  });
  // Default to 2 minutes.
  const [heartbeatInterval, setHeartbeatInterval] = useState<number | null>(
    null,
  );

  const makeRequest = useCallback(async () => {
    try {
      setResult(
        await handleDeviceDeleted(
          apiClient.deviceHeartbeat(heartbeatRequest),
          onDeviceDeleted,
        ),
      );
    } catch (err) {
      if (err instanceof Error) {
        logger.error(`Failed to fetch device heartbeat with '${err.message}'`);
        setError(err);
      } else {
        logger.error(
          `Failed to fetch device heartbeat with an unknown error: ${err}`,
        );
        setError(new Error('Unknown error'));
      }
    }
  }, [apiClient, onDeviceDeleted, logger, heartbeatRequest]);

  // We need to memoize the { message: <handler> } object because pubnub removes
  // listeners by reference of the object rather than the callback.
  const handleMessage = useMemo(
    () => ({
      message: (event: Pubnub.MessageEvent) => {
        if (
          event.message &&
          typeof event.message === 'object' &&
          event.message?.type === 'action' &&
          event.message?.body?.value === 'check_heartbeat'
        ) {
          logger.info(`Received 'check_hearbeat' from pubnub`);
          makeRequest();
        }
      },
    }),
    [makeRequest, logger],
  );

  // Call hearbeat immediately on mount.
  useEffect(() => {
    makeRequest();
  }, [makeRequest]);

  // Call heartbeat every 2 minutes unless interval is specified.
  useEffect(() => {
    const interval = setInterval(
      makeRequest,
      heartbeatInterval || 2 * 60 * 1000,
    );
    return () => {
      clearInterval(interval);
    };
  }, [makeRequest, heartbeatInterval]);

  // Listen for Soc device POST (heartbeat) message

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.id && event.data.id === 'soc-device-heartbeat-event') {
        const heartbeatData = event.data['info'];
        setHeartbeatRequest({
          connection_type: heartbeatData.connectionType,
          ssid: heartbeatData.ssid,
          rssi: heartbeatData.rssi,
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Listen for heartbeat messages.
  useEffect(() => {
    pubnubClient.addListener(handleMessage);

    return () => {
      pubnubClient.removeListener(handleMessage);
    };
  }, [pubnubClient, handleMessage]);

  return [result, error, setHeartbeatInterval];
}
